import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { DynamicForm } from "../../util/DynamicForm";
import { useGeneratedForm } from "../../hooks/useGeneratedForm";
import { apiPost } from "../../util/axiosUtil";
import API_URL from "../../config/apiConfig";
import { z } from "zod";
import MutationHandler from "../../util/MutationHandler";

const CREATE_SUPERMASTER_URL = `${API_URL}/api/super-masters`;

const formConfig = {
  name: {
    label: "Name",
    type: "text",
    placeholder: "Enter your name",
    defaultValue: "",
    required: true,
    validation: z.string().superRefine((value, ctx) => {
      if (!value.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Name is required",
        });
      } else if (value.length < 4) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Name should be more than 3 letters",
        });
      }
    }),
  },
  username: {
    label: "Username",
    type: "text",
    placeholder: "Enter username",
    defaultValue: "",
    required: true,
    normalize: (val) => val.toLowerCase().replace(/\s+/g, ""),
    validation: z
      .string()
      .transform((val) => val.toLowerCase().replace(/\s+/g, "")) // ✅ auto-make lowercase and remove spaces
      .superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Username is required",
          });
        } else if (value.length < 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Username should be more than 3 letters",
          });
        } else if (!/^[a-z0-9_]+$/.test(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Username can only contain lowercase letters, numbers, and underscores",
          });
        }
      }),
  },
  password: {
    label: "Password",
    type: "password",
    placeholder: "Enter password",
    defaultValue: "",
    required: true,
    normalize: (val) => val.replace(/\s+/g, ""),
    validation: z.string().min(4, "Password must be at least 4 characters"),
  },
  points: {
    label: "Points",
    type: "number",
    placeholder: "Enter points",
    required: true,
    defaultValue: 0,
    validation: z.preprocess(
      (val) => (val === "" ? 0 : Number(val)),
      z.number().superRefine((value, ctx) => {
        if (Number.isNaN(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Points must be a number",
          });
        } else if (value < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Points must be at least 1",
          });
        }
      })
    ),
  },
};

export default function TelecomSuperMasterForm({ onCancel, onSuccess }) {
  return (
    <MutationHandler
      mutationFn={(formData) => {
        const { superMaster, masters, ...postData } = formData;
        return apiPost(CREATE_SUPERMASTER_URL, postData);
      }}
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <TelecomSuperMaster
          formConfig={formConfig}
          onSubmit={(value) => mutation.mutate(value)}
          isLoading={mutation.isPending}
          onCancel={onCancel}
        />
      )}
    </MutationHandler>
  );
}
function TelecomSuperMaster({ formConfig, onSubmit, isLoading, onCancel }) {
  const form = useGeneratedForm(formConfig, async ({ value }) => {
    onSubmit(value);
  });

  return (
    <DynamicForm
      form={form}
      formConfig={formConfig}
      isLoading={isLoading}
      onCancel={onCancel}
      buttonText={"Create Super Master"}
    />
  );
}
